import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FiArrowLeft,
  FiCalendar,
  FiMapPin,
  FiCheck,
  FiAlertCircle,
  FiTruck,
  FiPlus,
  FiMinus,
} from "react-icons/fi";
import { mealsAPI, usersAPI, ordersAPI } from "../../services/api";
import LoadingSpinner from "../../components/LoadingSpinner";
import PaymentMethodSelector from "../../components/Payment/PaymentMethodSelector";
import toast from "react-hot-toast";

const Container = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 2rem 0;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: none;
  color: #666;
  font-size: 1rem;
  cursor: pointer;
  margin-bottom: 2rem;
  padding: 0.5rem 0;
  transition: color 0.2s;

  &:hover {
    color: #2d5016;
  }
`;

const CheckoutGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: 2rem;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const CheckoutForm = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
`;

const OrderSummary = styled.div`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d5016;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 1rem;
  font-size: 1rem;
`;

const OptionCard = styled.div`
  padding: 1.5rem;
  border: 2px solid ${(props) => (props.$selected ? "#2d5016" : "#e0e0e0")};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 1rem;
  background: ${(props) => (props.$selected ? "#f0f8f0" : "white")};

  &:hover {
    border-color: #2d5016;
  }

  h4 {
    margin: 0 0 0.5rem 0;
    color: #2d5016;
    font-size: 1.125rem;
  }

  p {
    margin: 0;
    color: #666;
    font-size: 0.875rem;
  }
`;

const AddressSelect = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 0.875rem;
  min-height: 100px;
  resize: vertical;
  margin-bottom: 1rem;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: #2d5016;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;

  &.primary {
    background: #2d5016;
    color: white;

    &:hover:not(:disabled) {
      background: #1a3a0e;
    }
  }

  &.secondary {
    background: #6c757d;
    color: white;

    &:hover:not(:disabled) {
      background: #545b62;
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const MealPreview = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #e0e0e0;

  img {
    width: 100%;
    height: 200px;
    object-fit: cover;
    border-radius: 12px;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #2d5016;
    margin-bottom: 0.5rem;
  }

  p {
    color: #666;
    font-size: 0.875rem;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
    font-weight: 700;
    font-size: 1.125rem;
    color: #2d5016;
    margin-top: 0.5rem;
    padding-top: 1rem;
    border-top: 2px solid #2d5016;
  }

  span:first-child {
    color: #666;
  }

  span:last-child {
    color: #333;
    font-weight: 500;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #dc3545;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const QuantityLabel = styled.label`
  font-weight: 600;
  color: #333;
  font-size: 1rem;
`;

const QuantityInput = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
`;

const QuantityButton = styled.button`
  background: #f8f9fa;
  border: none;
  padding: 0.75rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: #e9ecef;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityValue = styled.div`
  padding: 0.75rem 1.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: #333;
  min-width: 60px;
  text-align: center;
  border-left: 1px solid #e0e0e0;
  border-right: 1px solid #e0e0e0;
`;

const MealOrder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [fulfillmentMethod, setFulfillmentMethod] = useState("delivery");
  const [paymentMethod, setPaymentMethod] = useState("mmg");
  const [processing, setProcessing] = useState(false);
  const [quantity, setQuantity] = useState(1);

  // Fetch meal details
  const { data: mealData, isLoading: loadingMeal } = useQuery(
    ["meal", id],
    () => mealsAPI.getMeal(id),
    {
      enabled: !!id,
    }
  );

  // Fetch user addresses
  const {
    data: addressesData,
    isLoading: loadingAddresses,
  } = useQuery("addresses", () => usersAPI.getProfile());

  const meal = mealData?.data?.data?.meal;
  const addresses =
    addressesData?.data?.data?.user?.addresses?.filter(
      (addr) => addr.id !== null
    ) || [];

  // Set minimum date to today
  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const minDate = tomorrow.toISOString().split("T")[0];
    setDeliveryDate(minDate);
  }, []);

  // Set default address
  useEffect(() => {
    if (addresses.length > 0 && !selectedAddress) {
      const defaultAddress = addresses.find((addr) => addr.is_default) || addresses[0];
      setSelectedAddress(defaultAddress);
    }
  }, [addresses, selectedAddress]);

  // Set default fulfillment method based on meal options
  useEffect(() => {
    if (meal) {
      const offersDelivery = meal.offers_delivery === true || meal.offers_delivery === "true" || meal.offers_delivery === 1;
      const offersPickup = meal.offers_pickup === true || meal.offers_pickup === "true" || meal.offers_pickup === 1;
      
      if (offersDelivery) {
        setFulfillmentMethod("delivery");
      } else if (offersPickup) {
        setFulfillmentMethod("pickup");
      }
    }
  }, [meal]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (fulfillmentMethod === "delivery" && !selectedAddress) {
      toast.error("Please select a delivery address");
      return;
    }

    if (!deliveryDate) {
      toast.error("Please select a delivery date");
      return;
    }

    setProcessing(true);

    try {
      // Convert date to ISO8601 format
      const [year, month, day] = deliveryDate.split("-");
      const date = new Date(
        Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day))
      );
      const deliveryDateISO = date.toISOString();

      const baseOrderData = {
        mealId: parseInt(id),
        deliveryDate: deliveryDateISO,
        fulfillmentMethod: fulfillmentMethod,
        paymentMethod: paymentMethod,
      };

      // Only include special_instructions if it's not empty
      if (specialInstructions && specialInstructions.trim().length > 0) {
        baseOrderData.specialInstructions = specialInstructions.trim();
      }

      // Only include delivery address if delivery method is selected
      if (fulfillmentMethod === "delivery") {
        baseOrderData.deliveryAddressId = parseInt(selectedAddress.id);
      }

      // Create multiple orders based on quantity
      const orderPromises = [];
      for (let i = 0; i < quantity; i++) {
        orderPromises.push(ordersAPI.createMealOrder(baseOrderData));
      }

      if (paymentMethod === "mmg") {
        // For MMG payments, create orders first, then redirect to payment
        // TODO: Implement MMG payment flow for standalone meals
        await Promise.all(orderPromises);
        
        toast.success(
          `Successfully created ${quantity} order${quantity > 1 ? "s" : ""}! Payment processing (Development Mode).`
        );
        navigate("/orders");
      } else {
        // For cash payments, create orders directly
        await Promise.all(orderPromises);

        toast.success(
          `Successfully created ${quantity} order${quantity > 1 ? "s" : ""}! You will pay with cash upon delivery/pickup.`
        );
        navigate("/orders");
      }
    } catch (error) {
      console.error("Order creation error:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to create order";
      const validationErrors = error.response?.data?.errors;

      if (validationErrors && validationErrors.length > 0) {
        const errorDetails = validationErrors
          .map((err) => `${err.param}: ${err.msg}`)
          .join(", ");
        toast.error(`Validation failed: ${errorDetails}`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setProcessing(false);
    }
  };

  if (loadingMeal || loadingAddresses) {
    return (
      <Container>
        <Content>
          <LoadingSpinner />
        </Content>
      </Container>
    );
  }

  if (!meal) {
    return (
      <Container>
        <Content>
          <ErrorMessage>
            <FiAlertCircle size={48} />
            <div>
              <h2>Meal not found</h2>
              <p>The meal you're looking for doesn't exist.</p>
            </div>
          </ErrorMessage>
          <Button className="secondary" onClick={() => navigate("/meals")}>
            <FiArrowLeft />
            Back to Meals
          </Button>
        </Content>
      </Container>
    );
  }

  const basePrice = parseFloat(meal.base_price || 0);
  const deliveryFee = parseFloat(meal.delivery_fee || 0);
  const pricePerMeal = fulfillmentMethod === "delivery" ? basePrice + deliveryFee : basePrice;
  const totalAmount = pricePerMeal * quantity;
  const offersDelivery = meal.offers_delivery === true || meal.offers_delivery === "true" || meal.offers_delivery === 1;
  const offersPickup = meal.offers_pickup === true || meal.offers_pickup === "true" || meal.offers_pickup === 1;

  return (
    <Container>
      <Content>
        <BackButton onClick={() => navigate(-1)}>
          <FiArrowLeft />
          Back
        </BackButton>

        <CheckoutGrid>
          <CheckoutForm>
            <SectionTitle>Order Meal</SectionTitle>

            <form onSubmit={handleSubmit}>
              <FormSection>
                <Label>Quantity</Label>
                <QuantityControl>
                  <QuantityLabel>Quantity:</QuantityLabel>
                  <QuantityInput>
                    <QuantityButton
                      type="button"
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      disabled={quantity <= 1}
                    >
                      <FiMinus />
                    </QuantityButton>
                    <QuantityValue>{quantity}</QuantityValue>
                    <QuantityButton
                      type="button"
                      onClick={() => setQuantity(quantity + 1)}
                    >
                      <FiPlus />
                    </QuantityButton>
                  </QuantityInput>
                </QuantityControl>
              </FormSection>

              <FormSection>
                <Label>Choose Fulfillment Method</Label>
                {offersDelivery && (
                  <OptionCard
                    $selected={fulfillmentMethod === "delivery"}
                    onClick={() => setFulfillmentMethod("delivery")}
                  >
                    <h4>
                      <FiTruck /> Delivery
                    </h4>
                    <p>
                      Get your meal delivered to your doorstep
                    </p>
                  </OptionCard>
                )}
                {offersPickup && (
                  <OptionCard
                    $selected={fulfillmentMethod === "pickup"}
                    onClick={() => setFulfillmentMethod("pickup")}
                  >
                    <h4>
                      <FiMapPin /> Pickup
                    </h4>
                    <p>Pick up your meal at the vendor location</p>
                    {meal.pickup_address && (
                      <p style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "#2d5016" }}>
                        Location: {meal.pickup_address}
                      </p>
                    )}
                    {meal.pickup_instructions && (
                      <p style={{ marginTop: "0.5rem", fontSize: "0.875rem", color: "#666" }}>
                        Instructions: {meal.pickup_instructions}
                      </p>
                    )}
                  </OptionCard>
                )}
              </FormSection>

              {fulfillmentMethod === "delivery" && (
                <FormSection>
                  <Label>Delivery Address</Label>
                  {addresses.length > 0 ? (
                    <AddressSelect
                      value={selectedAddress?.id || ""}
                      onChange={(e) => {
                        const address = addresses.find(
                          (addr) => addr.id === parseInt(e.target.value)
                        );
                        setSelectedAddress(address);
                      }}
                      required
                    >
                      <option value="">Select an address</option>
                      {addresses.map((address) => (
                        <option key={address.id} value={address.id}>
                          {address.address_line1}
                          {address.address_line2 ? `, ${address.address_line2}` : ""},{" "}
                          {address.city}, {address.region}
                          {address.is_default ? " (Default)" : ""}
                        </option>
                      ))}
                    </AddressSelect>
                  ) : (
                    <p style={{ color: "#dc3545", marginBottom: "1rem" }}>
                      No addresses found. Please add an address in your profile.
                    </p>
                  )}
                </FormSection>
              )}

              <FormSection>
                <Label>
                  <FiCalendar /> Delivery/Pickup Date
                </Label>
                <DateInput
                  type="date"
                  value={deliveryDate}
                  onChange={(e) => setDeliveryDate(e.target.value)}
                  min={new Date(Date.now() + 86400000).toISOString().split("T")[0]}
                  required
                />
              </FormSection>

              <FormSection>
                <Label>Special Instructions (Optional)</Label>
                <TextArea
                  value={specialInstructions}
                  onChange={(e) => setSpecialInstructions(e.target.value)}
                  placeholder="Any special requests or dietary requirements..."
                />
              </FormSection>

              <FormSection>
                <Label>Payment Method</Label>
                <PaymentMethodSelector
                  selectedMethod={paymentMethod}
                  onMethodChange={setPaymentMethod}
                  fulfillmentMethod={fulfillmentMethod}
                />
              </FormSection>

              <Button
                type="submit"
                className="primary"
                disabled={processing || (fulfillmentMethod === "delivery" && !selectedAddress)}
              >
                {processing ? "Processing..." : "Place Order"}
              </Button>
            </form>
          </CheckoutForm>

          <OrderSummary>
            <SectionTitle>Order Summary</SectionTitle>
            <MealPreview>
              {meal.image_url && (
                <img src={meal.image_url} alt={meal.meal_name} />
              )}
              <h3>{meal.meal_name}</h3>
              <p>{meal.business_name}</p>
            </MealPreview>

            <SummaryItem>
              <span>Price per meal</span>
              <span>GYD ${Math.round(pricePerMeal).toLocaleString()}</span>
            </SummaryItem>
            {fulfillmentMethod === "delivery" && deliveryFee > 0 && (
              <SummaryItem>
                <span>Delivery Fee (per meal)</span>
                <span>GYD ${Math.round(deliveryFee).toLocaleString()}</span>
              </SummaryItem>
            )}
            <SummaryItem>
              <span>Quantity</span>
              <span>{quantity}</span>
            </SummaryItem>
            <SummaryItem>
              <span>Total</span>
              <span>GYD ${Math.round(totalAmount).toLocaleString()}</span>
            </SummaryItem>
          </OrderSummary>
        </CheckoutGrid>
      </Content>
    </Container>
  );
};

export default MealOrder;

